import React, { useRef } from "react";
import { X, Plus, Trash2, Image as ImageIcon } from "lucide-react";

export default function MeasurementBuilder({ measurements = [], onChange, onClose }) {
  const fileInputRefs = useRef({});
  const [previewUnit, setPreviewUnit] = React.useState("cm");

  const handleAddTable = () => {
    onChange([
      ...measurements,
      {
        title: "New Table",
        headers: ["Size", "Measurement"],
        data: [["", ""]],
        description: "",
        image: ""
      }
    ]);
  };

  const handleRemoveTable = (tIndex) => {
    onChange(measurements.filter((_, i) => i !== tIndex));
  };

  const handleTitleChange = (tIndex, val) => {
    const newM = [...measurements];
    newM[tIndex].title = val;
    onChange(newM);
  };

  const handleDescriptionChange = (tIndex, val) => {
    const newM = [...measurements];
    newM[tIndex].description = val;
    onChange(newM);
  };

  const handleAddColumn = (tIndex) => {
    const newM = [...measurements];
    newM[tIndex].headers.push("New Column");
    newM[tIndex].data = newM[tIndex].data.map(row => [...row, ""]);
    onChange(newM);
  };

  const handleRemoveColumn = (tIndex, cIndex) => {
    const newM = [...measurements];
    newM[tIndex].headers = newM[tIndex].headers.filter((_, i) => i !== cIndex);
    newM[tIndex].data = newM[tIndex].data.map(row => row.filter((_, i) => i !== cIndex));
    onChange(newM);
  };

  const handleAddRow = (tIndex) => {
    const newM = [...measurements];
    const columnCount = newM[tIndex].headers.length;
    newM[tIndex].data.push(Array(columnCount).fill(""));
    onChange(newM);
  };

  const handleRemoveRow = (tIndex, rIndex) => {
    const newM = [...measurements];
    newM[tIndex].data = newM[tIndex].data.filter((_, i) => i !== rIndex);
    onChange(newM);
  };

  const handleHeaderChange = (tIndex, cIndex, val) => {
    const newM = [...measurements];
    newM[tIndex].headers[cIndex] = val;
    onChange(newM);
  };

  const handleCellChange = (tIndex, rIndex, cIndex, val) => {
    const newM = [...measurements];
    newM[tIndex].data[rIndex][cIndex] = val;
    onChange(newM);
  };

  const handleImageUpload = (tIndex, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const newM = [...measurements];
      newM[tIndex].image = reader.result;
      onChange(newM);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = (tIndex) => {
    const newM = [...measurements];
    newM[tIndex].image = "";
    if (fileInputRefs.current[tIndex]) {
      fileInputRefs.current[tIndex].value = "";
    }
    onChange(newM);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gray-800 text-white px-6 py-4 flex justify-between items-center rounded-t-xl shrink-0">
          <h2 className="text-xl font-bold flex-1">MEASUREMENT CHARTS</h2>
          
          <div className="flex bg-gray-700/50 p-1 rounded-lg mr-6 border border-gray-600">
            <button 
              type="button"
              className={`px-3 py-1 text-xs font-bold rounded ${previewUnit === "cm" ? "bg-white text-gray-900" : "text-gray-400"}`}
              onClick={() => setPreviewUnit("cm")}
            >
              CM
            </button>
            <button 
              type="button"
              className={`px-3 py-1 text-xs font-bold rounded ${previewUnit === "inches" ? "bg-white text-gray-900" : "text-gray-400"}`}
              onClick={() => setPreviewUnit("inches")}
            >
              INCHES
            </button>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-gray-700 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 bg-gray-50">
          {measurements.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              No measurement tables added yet. Click "Add New Table" to start.
            </div>
          ) : (
            <div className="space-y-8">
              {measurements.map((table, tIndex) => (
                <div key={tIndex} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1 mr-4">
                      <label className="block text-sm font-bold text-gray-700 mb-1">Table Title</label>
                      <input
                        type="text"
                        value={table.title}
                        onChange={(e) => handleTitleChange(tIndex, e.target.value)}
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g. SALWAR TOP"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveTable(tIndex)}
                      className="text-red-500 hover:text-red-700 p-2 mt-5"
                      title="Remove Table"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Table Building */}
                  <div className="overflow-x-auto mb-4 border rounded-md">
                    <table className="w-full text-sm min-w-max">
                      <thead className="bg-gray-100">
                        <tr>
                          {table.headers.map((header, cIndex) => (
                            <th key={cIndex} className="p-2 border-b border-r bg-gray-200 min-w-[120px]">
                              <div className="flex flex-col items-center">
                                <input
                                  type="text"
                                  value={header}
                                  onChange={(e) => handleHeaderChange(tIndex, cIndex, e.target.value)}
                                  className="w-full bg-white px-2 py-1 border rounded text-center text-sm font-bold focus:outline-none mb-1"
                                />
                                {table.headers.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveColumn(tIndex, cIndex)}
                                    className="text-xs text-red-500 hover:text-red-700 mt-1"
                                  >
                                    Remove Col
                                  </button>
                                )}
                              </div>
                            </th>
                          ))}
                          <th className="w-10"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {table.data.map((row, rIndex) => (
                          <tr key={rIndex}>
                            {row.map((cell, cIndex) => (
                              <td key={cIndex} className="p-2 border-b border-r">
                                <input
                                  type="text"
                                  value={(() => {
                                    if (previewUnit === "inches" && cIndex !== 0) {
                                      const num = parseFloat(cell);
                                      return isNaN(num) ? cell : (num / 2.54).toFixed(1);
                                    }
                                    return cell;
                                  })()}
                                  readOnly={previewUnit === "inches"}
                                  onChange={(e) => handleCellChange(tIndex, rIndex, cIndex, e.target.value)}
                                  className={`w-full ${previewUnit === "inches" && cIndex !== 0 ? "bg-gray-50 text-blue-600 font-medium" : "bg-white"} px-2 py-1 border rounded text-center text-sm focus:outline-none focus:border-blue-500`}
                                  placeholder={previewUnit === "inches" && cIndex !== 0 ? "Calculated" : ""}
                                />
                              </td>
                            ))}
                            <td className="p-2 border-b text-center align-middle">
                              {table.data.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveRow(tIndex, rIndex)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4 mx-auto" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex gap-3 mb-6">
                    <button
                      type="button"
                      onClick={() => handleAddColumn(tIndex)}
                      className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" /> Add Column
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAddRow(tIndex)}
                      className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center gap-1"
                    >
                      <Plus className="w-4 h-4" /> Add Row
                    </button>
                  </div>

                  {/* Description & Image */}
                  <div className="grid md:grid-cols-2 gap-6 pt-4 border-t">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">Description / Instructions</label>
                      <textarea
                        value={table.description}
                        onChange={(e) => handleDescriptionChange(tIndex, e.target.value)}
                        className="w-full h-32 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                        placeholder="Add notes, e.g., 'Measurements in inches...'"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2">Associated Image</label>
                      {table.image ? (
                        <div className="relative inline-block">
                          <img src={table.image} alt="Measurement guide" className="max-h-32 rounded border" />
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(tIndex)}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 shadow-md"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center bg-gray-50 h-32 w-full max-w-sm">
                          <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
                          <button
                            type="button"
                            onClick={() => fileInputRefs.current[tIndex]?.click()}
                            className="px-4 py-1.5 bg-gray-900 text-white text-sm rounded hover:bg-gray-800 transition-colors"
                          >
                            Upload Image
                          </button>
                          <input
                            type="file"
                            ref={(el) => fileInputRefs.current[tIndex] = el}
                            onChange={(e) => handleImageUpload(tIndex, e.target.files[0])}
                            accept="image/*"
                            className="hidden"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <div className="mt-8 flex justify-center">
            <button
              type="button"
              onClick={handleAddTable}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2 font-medium transition-colors shadow-sm"
            >
              <Plus className="w-5 h-5" /> Add New Table
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white px-6 py-4 border-t flex justify-end shrink-0 rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors shadow-sm"
          >
            Save &amp; Close
          </button>
        </div>
      </div>
    </div>
  );
}
